/**
 * The application's cards, rebuilt in this system.
 *
 * These mirror what a paying customer sees today: the site card on the
 * dashboard, the panel cards on a domain, the action queue, the attention
 * banner and the plan bar. They exist here as a reference so the whole family
 * can be judged together before anything in the app is touched.
 *
 * Every legacy tell is gone on purpose: no eyebrow above the title, no
 * uppercase monospace micro-labels, no serif display face, no lift on hover,
 * and no developer vocabulary on a customer's screen.
 */
import type { ReactNode } from 'react'
import { useArrived } from './media'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/utils/cn'
import { Button, Proof, Readiness, Status, type StatusKind } from './primitives'
import './appcards.css'

/* ---------------- Measurements ---------------- */

/** A number out of a hundred. Ink, because a measurement is not a status. */
export function Metric({ value, of = 100, sm }: { value: number; of?: number; sm?: boolean }) {
  return (
    <div className={cn('ds-metric', sm && 'ds-metric--sm')}>
      {value}
      <span>/{of}</span>
    </div>
  )
}

export interface BarRow {
  label: string
  value: number
}

/** The five signals a scan reports, in the reader's words. */
export function Bars({ rows, lookBelow = 60 }: { rows: BarRow[]; lookBelow?: number }) {
  /* The fills grow once, when the group comes into view. See useArrived. */
  const [ref, arrived] = useArrived<HTMLDivElement>()
  return (
    <div className="ds-bars" ref={ref} data-arrived={arrived || undefined}>
      {rows.map((r) => (
        <div key={r.label} className="ds-bar" data-look={r.value < lookBelow || undefined}>
          <span className="ds-bar__label">{r.label}</span>
          <span className="ds-bar__track">
            {/* scaleX, not width: the system animates transforms, never
                layout. The track is the full width and the fill is scaled
                inside it. */}
            <span
              className="ds-bar__fill"
              style={{ ['--ds-bar-v' as string]: Math.max(0, Math.min(100, r.value)) / 100 }}
            />
          </span>
          <span className="ds-bar__value">{r.value}</span>
        </div>
      ))}
    </div>
  )
}

/** Which assistants were asked. Named, so the number means something. */
export function Asked({ names = ['ChatGPT', 'Claude', 'Gemini'], questions }: { names?: string[]; questions?: number }) {
  return (
    <div className="ds-asked">
      {names.map((n) => (
        <span key={n}>{n}</span>
      ))}
      {questions != null && <span>{questions} questions</span>}
    </div>
  )
}

/* ---------------- The site card ---------------- */

export type SiteState = 'ready' | 'attention' | 'needs-scan' | 'checking' | 'locked'

export interface SiteCardProps {
  name: string
  host: string
  state?: SiteState
  /** How AI assistants answer about this business, out of a hundred. */
  answerScore?: number
  recommend?: number
  mention?: number
  miss?: number
  /** How ready the site itself is. */
  readyScore?: number
  signals?: BarRow[]
  pagesSet?: number
  watching?: boolean
  checked?: string
  as?: 'div' | 'a'
  href?: string
}

const STATE_STATUS: Record<SiteState, StatusKind | null> = {
  ready: 'good',
  attention: 'look',
  'needs-scan': 'pending',
  checking: 'pending',
  locked: null,
}

export function SiteCard({
  name,
  host,
  state = 'ready',
  answerScore,
  recommend = 0,
  mention = 0,
  miss = 0,
  readyScore,
  signals,
  pagesSet,
  watching,
  checked,
  as = 'div',
  href,
}: SiteCardProps) {
  const status = STATE_STATUS[state]
  const Tag = as === 'a' ? 'a' : 'div'
  const hasAnswers = state === 'ready' || state === 'attention'

  return (
    <Tag
      className="ds-site"
      data-attention={state === 'attention' || undefined}
      {...(as === 'a' ? { href: href ?? '#' } : {})}
    >
      <div className="ds-site__head">
        <span className="ds-site__mark" aria-hidden="true">
          {name.slice(0, 1).toUpperCase()}
        </span>
        <span className="ds-site__id">
          <div className="ds-site__name">{name}</div>
          <div className="ds-site__host">{host}</div>
        </span>
        {status && <Status kind={status} size="sm" />}
      </div>

      <div className="ds-site__body">
        {/* What AI assistants say when someone asks about this business. */}
        <div className={cn('ds-site__tile', !hasAnswers && 'ds-site__tile--quiet')}>
          <div className="ds-site__tileHead">
            <span className="ds-site__label">How AI answers about you</span>
            {checked && hasAnswers && <span className="ds-site__when">{checked}</span>}
          </div>
          {hasAnswers && answerScore != null ? (
            <>
              <Metric value={answerScore} />
              <p className="ds-breakdown">
                <b>{recommend}</b> recommend you, <b>{mention}</b> mention you, <b>{miss}</b> do not.
              </p>
              <Asked questions={5} />
            </>
          ) : state === 'checking' ? (
            <p className="ds-breakdown" style={{ margin: 0 }}>
              Asking ChatGPT, Claude and Gemini about your business. This takes about a minute.
            </p>
          ) : state === 'locked' ? (
            <p className="ds-breakdown" style={{ margin: 0 }}>
              See whether AI assistants recommend you when someone asks. Included with any plan.
            </p>
          ) : (
            <p className="ds-breakdown" style={{ margin: 0 }}>
              Nobody has asked yet. One check takes about a minute.
            </p>
          )}
        </div>

        {/* How ready the site itself is. */}
        <div className={cn('ds-site__tile', !readyScore && 'ds-site__tile--quiet')}>
          <div className="ds-site__tileHead">
            <span className="ds-site__label">How ready your site is</span>
            {readyScore != null && <span className="ds-site__when">{readyScore} out of 100</span>}
          </div>
          {readyScore != null && signals ? (
            <Bars rows={signals} />
          ) : (
            <p className="ds-breakdown" style={{ margin: 0 }}>
              A check looks at whether AI agents can reach your pages, read them, and find your details.
            </p>
          )}
        </div>
      </div>

      <div className="ds-site__foot">
        <span>
          {pagesSet != null ? (
            <>
              <b>{pagesSet} pages</b> set up
            </>
          ) : (
            'No pages set up yet'
          )}
          {watching != null && <> · {watching ? 'Watching for changes' : 'Not watching yet'}</>}
        </span>
        <span className="ds-site__go" aria-hidden="true">
          <ArrowRight />
        </span>
      </div>
    </Tag>
  )
}

/* ---------------- The panel card ---------------- */

/**
 * One feature on a domain. The legacy version opened with an uppercase
 * monospace eyebrow and a serif title; the title carries itself here.
 */
export function PanelCard({
  title,
  line,
  children,
  note,
  action,
  className,
}: {
  title: string
  line: string
  children: ReactNode
  note?: ReactNode
  action?: ReactNode
  className?: string
}) {
  return (
    <section className={cn('ds-panel', className)}>
      <div className="ds-panel__head">
        <h3 className="ds-h3">{title}</h3>
        <p>{line}</p>
      </div>
      <div className="ds-panel__body">{children}</div>
      {(note || action) && (
        <div className="ds-panel__foot">
          <span>{note}</span>
          {action}
        </div>
      )}
    </section>
  )
}

/* ---------------- The action queue ---------------- */

export interface QueueItem {
  id: string
  title: string
  line: string
  cta: string
}

/** The next few things worth doing, most useful first. */
export function ActionQueue({ items, done }: { items: QueueItem[]; done?: boolean }) {
  if (done || items.length === 0) {
    return <Proof title="Nothing needs you right now" detail="We checked every page last night. You only hear from us if something changes." />
  }
  return (
    <ul className="ds-rows">
      {items.map((i) => (
        <li key={i.id} className="ds-row">
          <span className="ds-row__main">
            <div className="ds-row__title">{i.title}</div>
            <div className="ds-row__sub">{i.line}</div>
          </span>
          <Button size="sm" variant="secondary">
            {i.cta}
          </Button>
        </li>
      ))}
    </ul>
  )
}

/* ---------------- The plan bar ---------------- */

export function PlanBar({
  plan,
  sites,
  siteLimit,
  credits,
  action,
}: {
  plan: string
  sites: number
  siteLimit: number
  credits: number
  action?: ReactNode
}) {
  return (
    <div className="ds-planbar">
      <span>
        <b>{plan}</b> plan
      </span>
      <span className="ds-planbar__sep" aria-hidden="true" />
      <span>
        <b>
          {sites} of {siteLimit}
        </b>{' '}
        sites
      </span>
      <span className="ds-planbar__sep" aria-hidden="true" />
      <span>
        <b>{credits}</b> checks left this month
      </span>
      {action && <span className="ds-ml-auto">{action}</span>}
    </div>
  )
}

/* ---------------- Pages ready, as a composition of statuses ---------------- */

export function PagesReady({ good, look }: { good: number; look: number }) {
  return <Readiness good={good} look={look} />
}
